const computedKeys = new Set(['total_revenue', 'total_cogs', 'gross_profit', 'total_profit_sharing', 'total_opex', 'ebitda', 'total_below_ebitda', 'pbt', 'tax', 'pat']);
const number = value => value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);

// Monthly responses contain a tree; yearly responses contain stored rows.
// Categories already roll up their children, so never count both.
export function financialLines(data) {
  if (Array.isArray(data?.sections)) return data.sections.flatMap(section => (section.lines || [])
    .filter(line => line.kind !== 'computed' && !computedKeys.has(line.key))
    .map(line => ({ key: line.key, label: line.label, section: section.key, actual: number(line.actual), forecast: number(line.forecast) })));
  const rows = Array.isArray(data) ? data : data?.lines || [];
  const top = rows.filter(line => !line.parent_key && !line.parent && !String(line.line_key || line.key).includes('__') && !computedKeys.has(line.line_key || line.key));
  const normalized = top.map(line => {
    const key = line.line_key || line.key;
    const children = rows.filter(child => (child.parent_key || child.parent || String(child.line_key || child.key).split('__')[0]) === key && child !== line);
    const hasChildren = children.some(child => number(child.actual_amount ?? child.actual) != null || number(child.forecast_amount ?? child.forecast) != null);
    const amount = field => {
      if (!hasChildren) return number(line[`${field}_amount`] ?? line[field]);
      const values = children.map(child => number(child[`${field}_amount`] ?? child[field]));
      return values.every(v => v == null) ? null : values.reduce((sum, v) => sum + (v ?? 0), 0);
    };
    return { key, label: line.line_label || line.label, section: line.section, actual: amount('actual'), forecast: amount('forecast') };
  });
  const parents = new Set(top.map(line => line.line_key || line.key));
  for (const row of rows) {
    const key = row.line_key || row.key;
    const parent = row.parent_key || row.parent || (String(key).includes('__') ? String(key).split('__')[0] : null);
    if (parent && !parents.has(parent)) normalized.push({ key, label: row.line_label || row.label, section: row.section, actual: number(row.actual_amount ?? row.actual), forecast: number(row.forecast_amount ?? row.forecast) });
  }
  return normalized;
}
export const sectionLabels = { revenue: 'sales', cogs: 'direct costs', profit_sharing: 'profit sharing', opex: 'operating costs', below_ebitda: 'interest and depreciation' };
export function financialSummary(lines, field = 'actual') {
  const totals = {};
  for (const section of Object.keys(sectionLabels)) {
    const values = lines.filter(line => line.section === section).map(line => line[field]);
    totals[section] = values.some(v => v != null) ? values.reduce((s, v) => s + (v ?? 0), 0) : null;
  }
  const subtract = (first, ...rest) => [first, ...rest].some(v => v == null) ? null : rest.reduce((s, v) => s - v, first);
  const grossProfit = subtract(totals.revenue, totals.cogs);
  const ebitda = subtract(grossProfit, totals.profit_sharing, totals.opex);
  const pbt = subtract(ebitda, totals.below_ebitda);
  const costValues = [totals.cogs, totals.profit_sharing, totals.opex, totals.below_ebitda];
  return { ...totals, grossProfit, ebitda, pbt, costs: costValues.every(v => v != null) ? costValues.reduce((s, v) => s + v, 0) : null,
    missing: Object.keys(sectionLabels).filter(key => totals[key] == null) };
}
export function financialTrend(months, selectedMonth) {
  return Array.from({ length: 12 }, (_, i) => {
    const key = `${selectedMonth.slice(0, 4)}-${String(i + 1).padStart(2, '0')}`;
    const lines = financialLines(months?.[key] || []);
    const actual = financialSummary(lines), forecast = financialSummary(lines, 'forecast');
    const showActual = key <= selectedMonth;
    return { month: key, name: new Date(`${key}-01T12:00:00`).toLocaleDateString('en', { month: 'short' }),
      revenue: showActual ? actual.revenue : null, profit: showActual ? actual.grossProfit : null, ebitda: showActual ? actual.ebitda : null,
      forecastRevenue: forecast.revenue, forecastProfit: forecast.grossProfit, forecastEbitda: forecast.ebitda };
  });
}
